/**
 * Where the connect popup lands after the provider (ADR-029 §2). The server's
 * OAuth callback has already saved the connection — or not — and redirected
 * here with the outcome in the query; this page tells the sheet that opened
 * the popup (`ConnectAccountSheet`, on the `fias-connections` channel) and
 * closes. Nothing here acts: it only reports what the server did — so it is a
 * PUBLIC route (`App.tsx`): an expired session must not send the popup to the
 * sign-in page and leave the sheet waiting.
 *
 * It reports once (a ref, so React StrictMode's second effect run does not
 * post twice) but schedules the close on EVERY effect run: StrictMode runs the
 * effect, its cleanup (clearing the timer), and the effect again — a close
 * scheduled only on the first run would be cleared and never come back.
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CONNECTIONS_BROADCAST_CHANNEL } from '@/arche/components/ConnectAccountSheet';

export function ConnectionCompletePage() {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status') === 'connected' ? 'connected' : 'failed';
  const posted = useRef(false);

  useEffect(() => {
    if (!posted.current && typeof BroadcastChannel !== 'undefined') {
      posted.current = true;
      const channel = new BroadcastChannel(CONNECTIONS_BROADCAST_CHANNEL);
      channel.postMessage({
        status,
        provider: searchParams.get('provider') ?? undefined,
        connectionId: searchParams.get('connectionId') ?? undefined,
        reason: searchParams.get('reason') ?? undefined,
      });
      channel.close();
    }
    const timer = window.setTimeout(() => window.close(), 800);
    return () => window.clearTimeout(timer);
  }, [searchParams, status]);

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <p className="text-sm text-muted-foreground" role="status">
        {status === 'connected'
          ? 'Account connected. You can close this window.'
          : 'The account was not connected. You can close this window and try again.'}
      </p>
    </main>
  );
}
